import { buildRoomTitle } from 'shared/utils/room-title';

import type {
  HostMode,
  OrderBy,
  ResolvedSearchParams,
  WidgetAccommodation,
  WidgetCustomerInfo,
} from './types';

export interface ApiClientOptions {
  baseUrl: string;
  customerId: string;
}

interface SearchApiResponse {
  search?: unknown;
  accomodations: {
    results: AccomodationDTO[];
    count?: number;
    next?: string | null;
  };
}

interface ImageDTO {
  image?: string;
  preview?: string;
  large?: string;
  original?: string;
}

interface AccomodationDTO {
  source?: string;
  source_id?: string | null;
  property_info?: {
    name?: string;
    images?: ImageDTO[] | null;
    address?: { address_line?: string } | null;
  } | null;
  room_type_info?: {
    id?: string;
    name?: string;
    beds?: number | null;
    rooms?: number | null;
    area?: number | null;
    images?: ImageDTO[] | null;
    address?: { address_line?: string } | null;
  } | null;
  lowest_price_offer?: {
    price?: number;
    price_total?: number;
    days?: number;
  } | null;
}

export interface CityDTO {
  id: number;
  name: string;
  tl_city_id?: number | null;
  common_id?: number;
  rc_id?: number | null;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly customerId: string;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl;
    this.customerId = opts.customerId;
  }

  async getCustomerConfig(signal?: AbortSignal): Promise<WidgetCustomerInfo> {
    const res = await fetch(
      `${this.baseUrl}/widget/customers/${encodeURIComponent(this.customerId)}/config`,
      { signal, headers: { Accept: 'application/json' } },
    );
    if (!res.ok) {
      throw new Error(`customer_config_${res.status}`);
    }
    const json = (await res.json()) as {
      id: number;
      company_name: string;
      company_image: string | null;
      partner_code: string | null;
      booking_search_enabled: boolean;
    };
    return {
      id: json.id,
      companyName: json.company_name,
      companyImage: json.company_image,
      partnerCode: json.partner_code,
      bookingSearchEnabled: json.booking_search_enabled,
    };
  }

  async getCustomerCities(signal?: AbortSignal): Promise<CityDTO[]> {
    const res = await fetch(
      `${this.baseUrl}/widget/customers/${encodeURIComponent(this.customerId)}/cities`,
      { signal, headers: { Accept: 'application/json' } },
    );
    if (!res.ok) throw new Error(`customer_cities_${res.status}`);
    return (await res.json()) as CityDTO[];
  }

  async findCity(query: string, signal?: AbortSignal): Promise<CityDTO[]> {
    const url = new URL(`${this.baseUrl}/locations/cities`);
    if (query) url.searchParams.set('q', query);
    url.searchParams.set('limit', '10');
    const res = await fetch(url.toString(), {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`cities_${res.status}`);
    return (await res.json()) as CityDTO[];
  }

  async search(
    params: ResolvedSearchParams,
    mode: HostMode,
    orderBy?: OrderBy | null,
    signal?: AbortSignal,
  ): Promise<{ results: WidgetAccommodation[]; total: number }> {
    // /search публичный (optional_auth) — guest-токен (/auth/skip) больше не нужен.
    const url = new URL(`${this.baseUrl}/search`);
    url.searchParams.set('page', String(params.page));
    url.searchParams.set('size', String(params.pageSize));
    url.searchParams.set('host', this.customerId);
    url.searchParams.set('mode', mode);
    if (orderBy) url.searchParams.set('order_by', orderBy);

    // Backend expects an array of ages (0–17). When the embedder provides
    // explicit ages we pass them through; otherwise we send placeholders at
    // age 8 since availability search doesn't differentiate by exact age.
    const childrenAges =
      params.childrenAges && params.childrenAges.length
        ? params.childrenAges
        : Array.from({ length: params.children }, () => 8);

    const body = {
      params: {
        arrival_date: params.checkIn,
        departure_date: params.checkOut,
        adults: params.guests,
        children: childrenAges,
      },
      common_id: params.commonCityId ?? undefined,
      // Явные id источников — как в SPA: справочник городов расщеплён (RC- и
      // TL-строки одного города), и скоуп из одного common_id теряет TL или RC.
      tl_city_id: params.cityId ?? undefined,
      rc_id: params.rcId ?? undefined,
      sources: ['all'],
    };

    const res = await fetch(url.toString(), {
      method: 'POST',
      signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`search_${res.status}`);
    const json = (await res.json()) as SearchApiResponse;
    const results = (json.accomodations?.results ?? []).map(toAccommodation);
    const total = json.accomodations?.count ?? results.length;
    return { results, total };
  }
}

function pickImage(images: ImageDTO[] | null | undefined): string | null {
  if (!images || !images.length) return null;
  const first = images[0];
  // S3 returns multiple variants; prefer the lighter `preview` for card
  // thumbnails (~150KB) over `large`/`image` (~600KB+) to keep the widget
  // network footprint small on listing pages.
  return first.preview || first.image || first.large || first.original || null;
}

function toAccommodation(dto: AccomodationDTO): WidgetAccommodation {
  const room = dto.room_type_info ?? {};
  const property = dto.property_info ?? {};
  const offer = dto.lowest_price_offer ?? {};
  const photo = pickImage(room.images) ?? pickImage(property.images);
  const address =
    room.address?.address_line || property.address?.address_line || '';
  return {
    id: (room.id as string) ?? String(dto.source_id ?? ''),
    source: dto.source === 'realtycalendar' ? 'realtycalendar' : 'travelline',
    sourceId: dto.source_id ?? null,
    // Имя из PMS показываем, только если оно человеческое: у RC-хостов там
    // внутренний код («КРД_ЦП_Гарантия_4»), и на витрине партнёра он выглядел
    // как поломка. Площадь в подставленный заголовок добавляем намеренно, хоть
    // она и повторяется строкой ниже: без неё все квартиры хоста в городе
    // назывались одинаково («1-комнатная квартира»), и список карточек читался
    // как один объект, размноженный по адресам.
    title: buildRoomTitle({
      name: room.name || property.name,
      rooms: typeof room.rooms === 'number' ? room.rooms : null,
      area: typeof room.area === 'number' ? room.area : null,
      includeArea: true,
    }),
    address,
    photo,
    beds: typeof room.beds === 'number' ? room.beds : null,
    rooms: typeof room.rooms === 'number' ? room.rooms : null,
    area: typeof room.area === 'number' ? room.area : null,
    pricePerDay: offer.price ?? 0,
    priceTotal: offer.price_total ?? 0,
    // /search payload doesn't carry a currency field today; the project is
    // RUB-only end to end, so hardcode that here rather than guessing.
    currency: 'RUB',
  };
}
